import React, { useState, useRef, KeyboardEvent, useEffect } from 'react';
import { ICampaignFormData, QuestionType } from '../../interface/ICampaign';
import style from './CampaignForm.module.scss';
import {
	TextField,
	Select,
	MenuItem,
	Button,
	InputLabel,
	FormControl,
	Box,
	Checkbox,
	FormControlLabel,
	Grid,
	TableContainer,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Typography,
	IconButton,
	Alert,
} from '@mui/material';
import {
	Add as AddIcon,
	Edit as EditIcon,
	Delete as DeleteIcon,
	DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import BasicDatePicker from '../../../../core/components/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { GetRewards } from '../../../../core/API/workplaces';

interface CampaignFormProps {
	initialData?: ICampaignFormData;
	onSubmit: (data: ICampaignFormData) => void;
	onCancel: () => void;
	_availableRewards?: Array<{ id: string; name: string }>;
	workplaceId?: string;
}

interface RewardItem {
	'@id': string;
	id: number;
	title: string;
	description: string;
	active: boolean;
	workplace: string;
}

interface QuestionItem {
	id: string;
	questionText: string;
	questionType: QuestionType;
	sortOrder: number;
	isEditing: boolean;
	isSaved: boolean;
	isValid: {
		questionText: boolean;
		questionType: boolean;
	};
	errors: {
		questionText?: string;
		questionType?: string;
	};
	originalValues?: {
		questionText: string;
		questionType: QuestionType;
	};
}

export default function CampaignForm({
	initialData,
	onSubmit,
	onCancel,
	_availableRewards,
	workplaceId,
}: CampaignFormProps): JSX.Element {
	const [formData, setFormData] = useState<ICampaignFormData>(
		initialData || {
			title: '',
			workplaceId: workplaceId || '',
			startDate: '',
			endDate: '',
			questions: [],
			reward: undefined,
		}
	);

	const [startDate, setStartDate] = useState<Dayjs | null>(
		initialData?.startDate ? dayjs(initialData.startDate) : null
	);
	const [endDate, setEndDate] = useState<Dayjs | null>(
		initialData?.endDate ? dayjs(initialData.endDate) : null
	);

	const [rewardEnabled, setRewardEnabled] = useState(!!initialData?.reward?.enabled);
	const [rewards, setRewards] = useState<RewardItem[]>([]);
	const [isLoadingRewards, setIsLoadingRewards] = useState(false);
	const [selectedRewardId, setSelectedRewardId] = useState<string>('');

	// Fetch rewards from API
	useEffect(() => {
		const fetchRewards = async () => {
			setIsLoadingRewards(true);
			try {
				const rewardsData = await GetRewards(workplaceId);
				setRewards(rewardsData);

				// After rewards are fetched, set selectedRewardId if editing
				if (initialData?.reward) {
					const rewardId = (initialData.reward as any)['@id'] || initialData.reward.rewardId;
					if (rewardId) {
						setSelectedRewardId(rewardId);
					}
				}
			} catch (error) {
				console.error('Error fetching rewards:', error);
			} finally {
				setIsLoadingRewards(false);
			}
		};

		if (workplaceId) {
			fetchRewards();
		}
	}, [workplaceId, initialData?.reward]);

	// Sync selectedRewardId to formData when it changes
	useEffect(() => {
		if (selectedRewardId && rewardEnabled) {
			const selectedReward = rewards.find((r) => r['@id'] === selectedRewardId);
			if (selectedReward && formData.reward) {
				setFormData((prev) => ({
					...prev,
					reward: prev.reward
						? {
								...prev.reward,
								rewardId: selectedRewardId,
								rewardName: selectedReward.title,
							}
						: prev.reward,
				}));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedRewardId, rewards, rewardEnabled]);
	// Questions table state
	const [questions, setQuestions] = useState<QuestionItem[]>(() => {
		if (initialData?.questions && initialData.questions.length > 0) {
			return initialData.questions.map((q) => ({
				id: q.id,
				questionText: q.questionText,
				questionType: q.questionType,
				sortOrder: q.sortOrder,
				isEditing: false,
				isSaved: true,
				isValid: { questionText: true, questionType: true },
				errors: {},
			}));
		}
		return [createEmptyQuestion()];
	});

	const cellRefs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});
	const [activeCell, setActiveCell] = useState<{ rowIndex: number; colId: string } | null>(null);
	const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

	function createEmptyQuestion(): QuestionItem {
		return {
			id: `temp-${Date.now()}${Math.random().toString(36).substring(2, 9)}`,
			questionText: '',
			questionType: QuestionType.RATING,
			sortOrder: 0,
			isEditing: true,
			isSaved: false,
			isValid: { questionText: true, questionType: true },
			errors: {},
		};
	}

	const columns = [
		{ id: 'questionText', header: 'Question Text', validation: { required: true, maxLength: 500 } },
		{
			id: 'questionType',
			header: 'Question Type',
			validation: {
				required: true,
				options: [QuestionType.RATING, QuestionType.YES_NO, QuestionType.COMMENT],
			},
		},
	];

	const validateCell = (value: string, columnId: string): { valid: boolean; message?: string } => {
		const column = columns.find((col) => col.id === columnId);
		if (!column || !column.validation) return { valid: true };

		const { validation } = column;

		if (validation.required && !value.trim()) {
			return { valid: false, message: 'This field is required' };
		}
		if (validation.maxLength && value.trim().length > validation.maxLength) {
			return { valid: false, message: `Must be no more than ${validation.maxLength} characters` };
		}
		if (
			validation.options &&
			value.trim() &&
			!validation.options.includes(value.trim() as QuestionType)
		) {
			return { valid: false, message: `Must be one of the valid question types` };
		}

		return { valid: true };
	};

	const updateField = (id: string, field: string, value: string) => {
		setQuestions((prevQuestions) =>
			prevQuestions.map((item) => {
				if (item.id !== id) return item;
				const validation = validateCell(value, field);
				return {
					...item,
					[field]: value,
					isValid: { ...item.isValid, [field]: validation.valid },
					errors: { ...item.errors, [field]: validation.valid ? undefined : validation.message },
				};
			})
		);
	};

	const validateRow = (item: QuestionItem): { valid: boolean; updatedItem: QuestionItem } => {
		let allValid = true;
		const updatedItem = {
			...item,
			errors: {},
			isValid: { questionText: true, questionType: true },
		};

		['questionText', 'questionType'].forEach((field) => {
			const validation = validateCell(item[field as keyof QuestionItem] as string, field);
			updatedItem.isValid[field as keyof typeof item.isValid] = validation.valid;
			updatedItem.errors[field as keyof typeof item.errors] = validation.valid
				? undefined
				: validation.message;
			if (!validation.valid) {
				allValid = false;
			}
		});

		return { valid: allValid, updatedItem };
	};

	const toggleEditMode = (id: string) => {
		setQuestions((prevQuestions) =>
			prevQuestions.map((item) => {
				if (item.id === id) {
					const isEditing = !item.isEditing;
					if (isEditing) {
						const originalValues = {
							questionText: item.questionText,
							questionType: item.questionType,
						};
						const { updatedItem } = validateRow(item);
						return { ...updatedItem, isEditing, originalValues };
					} else {
						if (!item.isSaved) {
							return {
								...item,
								isEditing: false,
								isSaved: true,
								errors: {},
								isValid: { questionText: true, questionType: true },
							};
						}
						return { ...item, isEditing: false };
					}
				}
				return item;
			})
		);
	};

	const deleteRow = (id: string) => {
		setQuestions((prevQuestions) => prevQuestions.filter((item) => item.id !== id));
	};

	const cancelEdit = (id: string) => {
		setQuestions(
			(prevQuestions) =>
				prevQuestions
					.map((item) => {
						if (item.id === id) {
							if (item.isSaved && item.originalValues) {
								return {
									...item,
									isEditing: false,
									questionText: item.originalValues.questionText,
									questionType: item.originalValues.questionType,
									errors: {},
									isValid: { questionText: true, questionType: true },
									originalValues: undefined,
								};
							} else if (!item.isSaved) {
								return null;
							} else {
								return { ...item, isEditing: false };
							}
						}
						return item;
					})
					.filter(Boolean) as QuestionItem[]
		);
	};

	const addSingleRow = () => {
		const newRow = createEmptyQuestion();
		setQuestions((prevQuestions) => [...prevQuestions, newRow]);

		setTimeout(() => {
			const newRowIndex = questions.length;
			const questionTextRef = cellRefs.current[`${newRowIndex}-questionText`];
			if (questionTextRef) {
				questionTextRef.focus();
			}
		}, 100);
	};

	const moveRow = (dragIndex: number, hoverIndex: number) => {
		if (dragIndex === hoverIndex) return;

		setQuestions((prevQuestions) => {
			const updatedQuestions = [...prevQuestions];
			const [draggedItem] = updatedQuestions.splice(dragIndex, 1);
			updatedQuestions.splice(hoverIndex, 0, draggedItem);

			// Update sort orders
			return updatedQuestions.map((q, idx) => ({
				...q,
				sortOrder: idx,
			}));
		});
	};

	const handleDragStart = (e: React.DragEvent, index: number) => {
		setDraggedIndex(index);
		e.dataTransfer.effectAllowed = 'move';
		e.dataTransfer.setData('text/html', '');
	};

	const handleDragOver = (e: React.DragEvent, index: number) => {
		e.preventDefault();
		if (draggedIndex === null || draggedIndex === index) return;

		moveRow(draggedIndex, index);
		setDraggedIndex(index);
	};

	const handleDragEnd = () => {
		setDraggedIndex(null);
	};

	const handleCellFocus = (rowIndex: number, colId: string) => {
		setActiveCell({ rowIndex, colId });
	};

	const handleKeyDown = (e: KeyboardEvent<any>, rowIndex: number, colId: string) => {
		if (!activeCell) return;

		const { key } = e;
		let nextRow = activeCell.rowIndex;
		let nextColIndex = columns.findIndex((col) => col.id === activeCell.colId);
		let nextColId = colId;

		const lastColumnIndex = columns.length - 1;

		switch (key) {
			case 'ArrowDown':
				if (rowIndex < questions.length - 1) {
					nextRow = rowIndex + 1;
					nextColId = columns[0].id;
				} else if (rowIndex === questions.length - 1 && questions.length < 50) {
					addSingleRow();
					nextRow = questions.length;
					nextColId = columns[0].id;
				}
				e.preventDefault();
				break;
			case 'ArrowUp':
				if (nextRow > 0) {
					nextRow = nextRow - 1;
				}
				e.preventDefault();
				break;
			case 'Tab':
				if (e.shiftKey) {
					if (nextColIndex > 0) {
						nextColIndex -= 1;
						nextColId = columns[nextColIndex].id;
					} else if (rowIndex > 0) {
						nextRow = rowIndex - 1;
						nextColId = columns[lastColumnIndex].id;
					}
				} else {
					if (nextColIndex < lastColumnIndex) {
						nextColIndex += 1;
						nextColId = columns[nextColIndex].id;
					} else if (rowIndex < questions.length - 1) {
						nextRow = rowIndex + 1;
						nextColIndex = 0;
						nextColId = columns[0].id;
					} else if (questions.length < 50) {
						addSingleRow();
						nextRow = questions.length;
						nextColIndex = 0;
						nextColId = columns[0].id;
					}
				}
				e.preventDefault();
				break;
			case 'Enter':
				if (rowIndex < questions.length - 1) {
					nextRow = rowIndex + 1;
					nextColId = columns[0].id;
				} else if (rowIndex === questions.length - 1 && questions.length < 50) {
					addSingleRow();
					nextRow = questions.length;
					nextColId = columns[0].id;
				}
				e.preventDefault();
				break;
			default:
				return;
		}

		if (nextRow !== rowIndex || nextColId !== colId) {
			setTimeout(() => {
				const cellRefKey = `${nextRow}-${nextColId}`;
				const nextCellRef = cellRefs.current[cellRefKey];
				if (nextCellRef) nextCellRef.focus();
				setActiveCell({ rowIndex: nextRow, colId: nextColId });
			}, 0);
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | any) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleRewardToggle = (enabled: boolean) => {
		setRewardEnabled(enabled);
		if (!enabled) {
			setFormData((prev) => ({
				...prev,
				reward: undefined,
			}));
		} else {
			setFormData((prev) => ({
				...prev,
				reward: {
					'@id': '',
					rewardName: '',
					numberOfWinners: 1,
					rewardDetailsText: '',
					enabled: true,
				},
			}));
		}
	};

	const handleRewardChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			reward: prev.reward
				? {
						...prev.reward,
						[name]: name === 'numberOfWinners' ? (value === '' ? '' : parseInt(value)) : value,
					}
				: undefined,
		}));
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (!workplaceId) {
			alert('Workplace not found. Please try again.');
			return;
		}

		// Validate and convert questions
		const validQuestions = questions
			.filter((q) => q.isSaved || (q.questionText && q.questionType))
			.map((q, index) => ({
				id: q.id.startsWith('temp-') ? undefined : q.id, // Only include id if it's not a new unsaved question
				questionText: q.questionText,
				questionType: q.questionType,
				sortOrder: index,
			}));

		if (validQuestions.length === 0) {
			alert('Please add at least one question');
			return;
		}

		// Validate reward if enabled
		if (rewardEnabled && formData.reward) {
			const numberOfWinners =
				typeof formData.reward.numberOfWinners === 'string'
					? parseInt(formData.reward.numberOfWinners)
					: formData.reward.numberOfWinners;

			if (!numberOfWinners || numberOfWinners < 1) {
				alert('Number of winners must be at least 1');
				return;
			}

			if (numberOfWinners > 100) {
				alert('Number of winners cannot exceed 100');
				return;
			}
		}

		const submissionData = {
			...formData,
			startDate: startDate ? startDate.format('YYYY-MM-DD') : '',
			endDate: endDate ? endDate.format('YYYY-MM-DD') : '',
			workplace: `/api/workplaces/${workplaceId}`,
			questions: validQuestions,
		};
		console.log('Submitting Campaign Data:', submissionData);

		onSubmit(submissionData);
	};

	const isRowLimitReached = questions.length >= 50;

	return (
		<div className={style.editCampaignForm}>
			<Grid container direction="column" spacing={2} p={5}>
				<Grid container direction="row" justifyContent="space-between">
					<Grid>
						<Button className={style.backButton} onClick={onCancel}>
							Back
						</Button>
					</Grid>
					<Grid item xs={6} display={'flex'} justifyContent="flex-end">
						<Button className={style.backButton} onClick={handleSubmit}>
							{initialData ? 'Save' : 'Create'}
						</Button>
					</Grid>
				</Grid>
				<Grid container direction="row" justifyContent="flex-start" alignItems="center">
					<h2 className={style.formTitle}>
						{initialData ? 'Edit Campaign' : 'Create New Campaign'}
					</h2>
				</Grid>
			</Grid>

			<form className={style.formContainer} onSubmit={handleSubmit}>
				<TextField
					label="Campaign Title"
					name="title"
					value={formData.title}
					onChange={handleInputChange}
					className={style.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ maxLength: 200 }}
				/>

				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<BasicDatePicker
							label="Start Date"
							value={startDate}
							onChange={(date) => setStartDate(date)}
							required
							sx={{ fontFamily: 'Gotham', paddingBottom: '12px', width: '100%' }}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<BasicDatePicker
							label="End Date"
							value={endDate}
							onChange={(date) => setEndDate(date)}
							required
							sx={{ fontFamily: 'Gotham', paddingBottom: '12px', width: '100%' }}
						/>
					</Grid>
				</Grid>

				{/* Questions Section - Table Based */}
				<Box sx={{ mt: 3, mb: 2 }}>
					<h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', fontWeight: 600 }}>
						Campaign Questions
					</h3>

					<TableContainer
						className={style.consistentTableContainer}
						style={{ maxHeight: 'calc(100vh - 250px)', marginTop: '20px' }}
					>
						<Table className={style.consistentTable} stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell padding="checkbox" sx={{ width: '40px' }}></TableCell>
									{columns.map((column) => (
										<TableCell key={column.id} sx={{ p: 1 }}>
											<Box sx={{ display: 'flex', alignItems: 'center' }}>
												{column.header}
												{column.validation?.required && (
													<Typography component="span" color="error" sx={{ ml: 0.5 }}>
														*
													</Typography>
												)}
											</Box>
										</TableCell>
									))}
									<TableCell sx={{ width: '150px' }}>Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{questions.map((item, index) => (
									<TableRow
										key={item.id}
										hover
										draggable
										onDragStart={(e) => handleDragStart(e, index)}
										onDragOver={(e) => handleDragOver(e, index)}
										onDragEnd={handleDragEnd}
										sx={{
											opacity: draggedIndex === index ? 0.5 : 1,
											cursor: 'move',
										}}
									>
										<TableCell padding="checkbox">
											<DragIndicatorIcon
												sx={{
													color: 'action.disabled',
													cursor: 'grab',
													'&:active': { cursor: 'grabbing' },
												}}
											/>
										</TableCell>
										<TableCell>
											{item.isEditing ? (
												<TextField
													inputRef={(el) => (cellRefs.current[`${index}-questionText`] = el)}
													value={item.questionText}
													onChange={(e) => updateField(item.id, 'questionText', e.target.value)}
													onFocus={() => handleCellFocus(index, 'questionText')}
													onKeyDown={(e) => handleKeyDown(e, index, 'questionText')}
													error={!item.isValid.questionText}
													helperText={item.errors.questionText}
													fullWidth
													size="small"
													inputProps={{ maxLength: 500 }}
												/>
											) : (
												<Typography variant="body2">{item.questionText}</Typography>
											)}
										</TableCell>
										<TableCell>
											{item.isEditing ? (
												<Select
													inputRef={(el) => (cellRefs.current[`${index}-questionType`] = el)}
													value={item.questionType}
													onChange={(e) => updateField(item.id, 'questionType', e.target.value)}
													onFocus={() => handleCellFocus(index, 'questionType')}
													onKeyDown={(e) => handleKeyDown(e, index, 'questionType')}
													error={!item.isValid.questionType}
													fullWidth
													size="small"
												>
													<MenuItem value={QuestionType.RATING}>Rating (1-5)</MenuItem>
													<MenuItem value={QuestionType.YES_NO}>Yes/No</MenuItem>
													<MenuItem value={QuestionType.COMMENT}>Comment</MenuItem>
												</Select>
											) : (
												<Typography variant="body2">
													{item.questionType === QuestionType.RATING
														? 'Rating (1-5)'
														: item.questionType === QuestionType.COMMENT
															? 'Comment'
															: 'Yes/No'}
												</Typography>
											)}
										</TableCell>
										<TableCell>
											<Box sx={{ display: 'flex', gap: 0.5 }}>
												{item.isEditing ? (
													<>
														<IconButton
															size="small"
															onClick={() => cancelEdit(item.id)}
															title="Delete"
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</>
												) : (
													<>
														<IconButton
															size="small"
															onClick={() => toggleEditMode(item.id)}
															title="Edit"
														>
															<EditIcon fontSize="small" />
														</IconButton>
														<IconButton
															size="small"
															onClick={() => deleteRow(item.id)}
															title="Delete"
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</>
												)}
											</Box>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>

					{questions.length === 0 && (
						<Box sx={{ p: 4, textAlign: 'center' }}>
							<Typography variant="body1" color="text.secondary">
								No questions added yet. Click &ldquo;Add Question&rdquo; to create your first
								question.
							</Typography>
						</Box>
					)}

					<Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
						<Box
							sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
						>
							<Button
								style={{
									backgroundColor: isRowLimitReached ? 'gray' : 'rgb(68, 99, 136)',
									color: 'white',
									padding: '5px 15px',
									fontSize: '12px',
								}}
								startIcon={<AddIcon />}
								onClick={addSingleRow}
								disabled={isRowLimitReached}
							>
								Add Question
							</Button>
							<Typography variant="caption" color="text.secondary">
								{questions.filter((q) => q.isSaved || (q.questionText && q.questionType)).length}/50
								questions
							</Typography>
						</Box>
					<Alert severity="info" sx={{ mt: 2, mb: 1 }}>
						<Typography variant="body2" sx={{ fontWeight: 500 }}>
							<strong>Important:</strong> For yes/no questions, frame them so that &ldquo;Yes&rdquo; indicates positive feedback
							and &ldquo;No&rdquo; indicates negative feedback. These answers contribute to the overall campaign score
							(Yes = higher score, No = lower score).
						</Typography>
					</Alert>
					<Typography variant="caption" color="text.secondary">
						Pro tip: Use Tab, Enter, and arrow keys to navigate between cells. Fields marked with * are required.
						</Typography>
					</Box>
				</Box>

				{/* Reward Settings */}
				<Box sx={{ mt: 3 }}>
					<h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', fontWeight: 600 }}>
						Reward Settings
					</h3>

					<Box
						sx={{
							border: '1px solid #ccc',
							borderRadius: 2,
							padding: 2,
							marginY: 2,
							color: 'rgb(0 0 0 / 60%)',
							display: 'inline-block',
						}}
					>
						<FormControlLabel
							control={
								<Checkbox
									checked={rewardEnabled}
									onChange={(e) => handleRewardToggle(e.target.checked)}
									sx={{
										color: 'rgb(68, 99, 136)',
										'&.Mui-checked': {
											color: 'rgb(68, 99, 136)',
										},
									}}
								/>
							}
							label="Enable Reward for this Campaign"
						/>
					</Box>
					{rewardEnabled && formData.reward && (
						<>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<FormControl
										fullWidth
										margin="normal"
										required
										className={style.input}
										disabled={isLoadingRewards}
									>
										<InputLabel id="reward-label">Select Reward</InputLabel>
										<Select
											labelId="reward-label"
											id="rewardId"
											name="rewardId"
											value={selectedRewardId}
											label="Select Reward"
											onChange={(e) => {
												const selectedReward = rewards.find((r) => r['@id'] === e.target.value);
												setSelectedRewardId(e.target.value);
												if (selectedReward) {
													setFormData((prev) => ({
														...prev,
														reward: prev.reward
															? {
																	...prev.reward,
																	rewardId: e.target.value,
																	rewardName: selectedReward.title,
																	rewardDetailsText: selectedReward.description || '',
																}
															: undefined,
													}));
												}
											}}
										>
											<MenuItem value="">
												{isLoadingRewards ? 'Loading rewards...' : 'Select a Reward'}
											</MenuItem>
											{rewards.map((reward) => (
												<MenuItem key={reward['@id']} value={reward['@id']}>
													{reward.title}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										label="Number of Winners"
										type="number"
										name="numberOfWinners"
										value={formData.reward.numberOfWinners}
										onChange={handleRewardChange}
										className={style.input}
										margin="normal"
										fullWidth
										required
										helperText="Enter a number between 1 and 100"
									/>
								</Grid>
							</Grid>
							<TextField
								label="Reward Details Text"
								name="rewardDetailsText"
								value={formData.reward.rewardDetailsText || ''}
								onChange={handleRewardChange}
								className={style.input}
								margin="normal"
								fullWidth
								multiline
								rows={4}
								placeholder="Enter details that will be displayed to customers"
								inputProps={{ maxLength: 1000 }}
							/>
						</>
					)}
				</Box>
			</form>
		</div>
	);
}
